// forecastShared.js
//
// Shared utilities and small primitives for ForecastView and CompareView.
// Mirrors patterns from PtpDashboard / PaymentsDashboard but adapted for
// the dual-window (historical + future) date logic the forecast pages need.
//
// Inlined date-range presets match the existing PBI dashboards so users
// see the same pills they're already used to.

import React from "react";
import { Box, Typography } from "@mui/material";

// ── Theme palette (kept consistent with PBI dashboards) ────────────
export const BLUE   = "#378ADD";
export const GREEN  = "#1D9E75";
export const AMBER  = "#BA7517";
export const PINK   = "#D4537E";
export const PURPLE = "#7F77DD";
export const RED    = "#A32D2D";

export const RANGE_OPTIONS = [
  "1D", "2D", "3D",
  "7D", "30D", "90D", "1Y",
  "C Wk", "CW+Last",
  "This Mo", "Cur+Last", "Prev 2Mo", "Prev 3Mo",
];

export const GRAN_OPTIONS = ["Day", "Week", "Month"];

// ── Helpers ────────────────────────────────────────────────────────
export function safeNum(v) { const n = Number(v); return isNaN(n) ? 0 : n; }

export function fmtCurrency(n) {
  const num = Number(n);
  if (!num || isNaN(num)) return "$0";
  if (Math.abs(num) >= 1_000_000) return `$${(num/1_000_000).toFixed(1)}M`;
  if (Math.abs(num) >= 1_000)     return `$${(num/1_000).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
}

export function fmtPct(v, dec = 2) {
  const n = Number(v);
  if (isNaN(n)) return "—";
  return `${n.toFixed(dec)}%`;
}

export function fmtDate(val) {
  if (!val) return "";
  const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });
}

function toLocalDateString(d) {
  const yy = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mo}-${dy}`;
}

// ── Forecast date-range logic ──────────────────────────────────────
// Unlike the PBI dashboards (historical only), forecast pages show a
// SYMMETRIC window: same span backward (historical actuals) AND forward
// (predicted) from today. So "7D" = past 7 days + next 7 days.
//
// Months-based presets (This Mo, Cur+Last, etc.) anchor on whole months
// for the historical side and project the same span forward.
//
// Returns: { historicalStart, historicalEnd, forecastStart, forecastEnd }
// All ISO date strings (YYYY-MM-DD), local timezone.
export function getForecastDateWindow(preset) {
  const now = new Date();
  const y   = now.getFullYear();
  const m   = now.getMonth();

  const dow        = now.getDay();
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - ((dow + 6) % 7));
  thisMonday.setHours(0, 0, 0, 0);
  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);

  let hStart, hEnd, fStart, fEnd;
  hEnd   = now;
  fStart = new Date(now); fStart.setDate(now.getDate() + 1);

  switch (preset) {
    case "1D":  hStart = addDays(now, -1);   fEnd = addDays(now,  1); break;
    case "2D":  hStart = addDays(now, -2);   fEnd = addDays(now,  2); break;
    case "3D":  hStart = addDays(now, -3);   fEnd = addDays(now,  3); break;
    case "7D":  hStart = addDays(now, -7);   fEnd = addDays(now,  7); break;
    case "30D": hStart = addDays(now, -30);  fEnd = addDays(now, 30); break;
    case "90D": hStart = addDays(now, -90);  fEnd = addDays(now, 90); break;
    case "1Y":  hStart = addDays(now, -365); fEnd = addDays(now, 365); break;
    case "C Wk":     hStart = thisMonday;            fEnd = addDays(now, 7); break;
    case "CW+Last":  hStart = lastMonday;            fEnd = addDays(now, 14); break;
    case "This Mo":  hStart = new Date(y, m, 1);     fEnd = new Date(y, m + 1, 0); break;
    case "Cur+Last": hStart = new Date(y, m - 1, 1); fEnd = new Date(y, m + 1, 0); break;
    case "Prev 2Mo": hStart = new Date(y, m - 2, 1); hEnd = new Date(y, m, 0);
                     fStart = new Date(y, m, 1);     fEnd = new Date(y, m + 2, 0); break;
    case "Prev 3Mo":
    default:         hStart = new Date(y, m - 3, 1); hEnd = new Date(y, m, 0);
                     fStart = new Date(y, m, 1);     fEnd = new Date(y, m + 3, 0); break;
  }

  return {
    historicalStart: toLocalDateString(hStart),
    historicalEnd:   toLocalDateString(hEnd),
    forecastStart:   toLocalDateString(fStart),
    forecastEnd:     toLocalDateString(fEnd),
  };
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}


// ── Reusable primitives ────────────────────────────────────────────

// Pill-style segmented control for single-select. Matches PtpDashboard's
// PillGroup so the visual rhythm carries across the app.
//
// `groups` is an optional array of segment sizes for visual dividers,
// e.g. [3, 4, 2, 4] inserts a 1px line after the 3rd, 7th, and 9th pill.
export function PillGroup({ label, options, value, onChange, groups }) {
  const groupBoundaries = new Set();
  if (groups) {
    let idx = 0;
    groups.forEach((size, gi) => {
      if (gi < groups.length - 1) { idx += size; groupBoundaries.add(idx); }
    });
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {label && (
        <Typography sx={{ fontSize: 11, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {label}
        </Typography>
      )}
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {options.map((opt, i) => {
          const active = value === opt;
          return (
            <React.Fragment key={opt}>
              {groupBoundaries.has(i) && (
                <Box sx={{ width: "1px", height: 18, bgcolor: "divider", mx: 0.5, flexShrink: 0 }} />
              )}
              <Box onClick={() => onChange(opt)} sx={{
                px: "10px", py: "3px", fontSize: 12, borderRadius: "20px",
                cursor: "pointer", userSelect: "none", border: "0.5px solid",
                borderColor: active ? BLUE : "divider",
                background:  active ? BLUE : "transparent",
                color:       active ? "#fff" : "text.secondary",
                fontWeight:  active ? 500 : 400,
                transition: "all .15s",
                "&:hover": { borderColor: BLUE },
              }}>{opt}</Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

// Multi-select pill group - used by the CompareView model picker.
// `selected` is an array of values; clicking toggles membership.
export function PillMultiGroup({ label, options, selected = [], onChange, accentColor = BLUE }) {
  const isSelected = (v) => selected.includes(v);
  const toggle = (v) => {
    if (isSelected(v)) onChange(selected.filter(s => s !== v));
    else               onChange([...selected, v]);
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {label && (
        <Typography sx={{ fontSize: 11, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {label}
        </Typography>
      )}
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {options.length === 0 && (
          <Typography sx={{ fontSize: 11, color: "text.disabled", py: "3px" }}>
            No options
          </Typography>
        )}
        {options.map(opt => {
          const active = isSelected(opt.value);
          return (
            <Box key={opt.value} onClick={() => toggle(opt.value)} sx={{
              px: "10px", py: "3px", fontSize: 12, borderRadius: "20px",
              cursor: "pointer", userSelect: "none", border: "0.5px solid",
              borderColor: active ? accentColor : "divider",
              background:  active ? accentColor : "transparent",
              color:       active ? "#fff" : "text.secondary",
              fontWeight:  active ? 500 : 400,
              transition: "all .15s",
              "&:hover": { borderColor: accentColor },
            }}>
              {opt.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}


// ── MultiSelectDropdown ────────────────────────────────────────────
// Same data shape as PillMultiGroup (options: [{value, label}], selected
// array of values, onChange fired with new array) but renders as a
// dropdown rather than inline pills. Use this when the option list could
// grow large (e.g., model lists with many models). Has built-in
// All / None / search + checkbox rows.
//
// Click trigger → menu opens beneath. Closes on outside-click or Esc.
// Selected count is shown on the trigger when collapsed.
export function MultiSelectDropdown({
  label,
  options,
  selected = [],
  onChange,
  accentColor = BLUE,
  placeholder = "Select…",
  searchable = true,
  minWidth = 220,
}) {
  const [open, setOpen]     = React.useState(false);
  const [query, setQuery]   = React.useState("");
  const triggerRef          = React.useRef(null);
  const menuRef             = React.useRef(null);

  const isSelected = (v) => selected.includes(v);

  const toggle = (v) => {
    if (isSelected(v)) onChange(selected.filter(s => s !== v));
    else               onChange([...selected, v]);
  };

  const filteredOptions = React.useMemo(() => {
    if (!searchable || !query) return options;
    const q = query.toLowerCase();
    return options.filter(o => String(o.label).toLowerCase().includes(q));
  }, [options, query, searchable]);

  const allValues       = options.map(o => o.value);
  const allSelected     = options.length > 0 && selected.length === options.length;
  const someSelected    = selected.length > 0 && !allSelected;

  // Selected summary shown on the trigger:
  //   "All models" / "3 models" / one label / "Select…"
  const triggerSummary = (() => {
    if (selected.length === 0)         return placeholder;
    if (allSelected)                   return `All ${options.length}`;
    if (selected.length === 1) {
      const o = options.find(o => o.value === selected[0]);
      return o ? o.label : "1 selected";
    }
    return `${selected.length} selected`;
  })();

  // Close on outside click + Esc
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target))    return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown",   onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown",   onKey);
    };
  }, [open]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, position: "relative" }}>
      {label && (
        <Typography sx={{ fontSize: 11, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {label}
        </Typography>
      )}

      {/* Trigger */}
      <Box
        ref={triggerRef}
        onClick={() => setOpen(o => !o)}
        sx={{
          minWidth,
          display: "flex", alignItems: "center", gap: 1,
          px: "12px", py: "5px",
          fontSize: 12, borderRadius: "20px",
          cursor: "pointer", userSelect: "none",
          border: "0.5px solid",
          borderColor: open ? accentColor : "divider",
          background:  selected.length > 0 ? accentColor : "transparent",
          color:       selected.length > 0 ? "#fff" : "text.secondary",
          fontWeight:  selected.length > 0 ? 500 : 400,
          transition: "all .15s",
          "&:hover": { borderColor: accentColor },
        }}
      >
        <Box sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {triggerSummary}
        </Box>
        <Box sx={{
          fontSize: 9, ml: 0.5,
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.15s ease",
        }}>▼</Box>
      </Box>

      {/* Menu */}
      {open && (
        <Box
          ref={menuRef}
          sx={{
            position: "absolute", top: "100%", left: 0, mt: 0.5,
            minWidth, maxWidth: 360,
            background: "#fff",
            border: "0.5px solid", borderColor: "divider",
            borderRadius: 2,
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            zIndex: 1300,
            overflow: "hidden",
          }}
        >
          {/* Search bar */}
          {searchable && options.length > 5 && (
            <Box sx={{ p: 1, borderBottom: "0.5px solid", borderColor: "divider" }}>
              <Box
                component="input"
                placeholder="Filter…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
                sx={{
                  width: "100%", fontSize: 12,
                  border: "0.5px solid", borderColor: "divider",
                  borderRadius: "12px", px: 1.25, py: "4px",
                  outline: "none",
                  "&:focus": { borderColor: accentColor },
                }}
              />
            </Box>
          )}

          {/* All / None toggle */}
          <Box
            onClick={() => onChange(allSelected ? [] : allValues)}
            sx={{
              display: "flex", alignItems: "center", gap: 1,
              px: 1.25, py: "6px",
              fontSize: 11, fontWeight: 500,
              color: accentColor, cursor: "pointer",
              borderBottom: "0.5px solid", borderColor: "divider",
              "&:hover": { background: `${accentColor}10` },
            }}
          >
            <Box sx={{
              width: 12, height: 12,
              border: "1px solid", borderColor: accentColor,
              borderRadius: "2px", display: "flex",
              alignItems: "center", justifyContent: "center",
              background: allSelected ? accentColor : (someSelected ? accentColor : "transparent"),
              color: "#fff", fontSize: 9, lineHeight: 1,
            }}>
              {allSelected ? "✓" : someSelected ? "–" : ""}
            </Box>
            {allSelected ? "Clear all" : "Select all"}
          </Box>

          {/* Options - scroll past ~8 items */}
          <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
            {filteredOptions.length === 0 ? (
              <Box sx={{ p: 1.5, fontSize: 11, color: "text.disabled", textAlign: "center" }}>
                No matches
              </Box>
            ) : (
              filteredOptions.map(opt => {
                const active = isSelected(opt.value);
                return (
                  <Box
                    key={opt.value}
                    onClick={() => toggle(opt.value)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1,
                      px: 1.25, py: "6px",
                      fontSize: 12, cursor: "pointer",
                      color: active ? "text.primary" : "text.secondary",
                      "&:hover": { background: "rgba(0,0,0,0.03)" },
                    }}
                  >
                    <Box sx={{
                      width: 12, height: 12,
                      border: "1px solid", borderColor: active ? accentColor : "divider",
                      borderRadius: "2px",
                      background: active ? accentColor : "transparent",
                      color: "#fff", fontSize: 9, lineHeight: 1,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {active ? "✓" : ""}
                    </Box>
                    {opt.label}
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// Top-right action button (pill style). Used for "+ New X", "↻ Regenerate",
// "▶ Run Forecast", etc. on each tab.
export function ActionButton({ children, onClick, color = BLUE, icon, variant = "outlined" }) {
  const filled = variant === "contained";
  return (
    <Box
      onClick={onClick}
      sx={{
        px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
        cursor: "pointer", userSelect: "none",
        border: "0.5px solid", borderColor: color,
        background:  filled ? color : "transparent",
        color:       filled ? "#fff" : color,
        fontWeight: 500,
        display: "inline-flex", alignItems: "center", gap: "6px",
        transition: "all .15s",
        "&:hover": { background: filled ? color : `${color}15` },
      }}
    >
      {icon && <span style={{ fontSize: 14, lineHeight: 1 }}>{icon}</span>}
      {children}
    </Box>
  );
}

// Small KPI tile — same shape as PBI dashboards' KpiTile but trimmed
// since forecast views don't need the full hero/amount/count stack yet.
export function ForecastKpiTile({ label, value, sublabel, color = BLUE }) {
  return (
    <Box sx={{
      background: "var(--mui-palette-background-default, #f7f7f7)",
      borderRadius: 2,
      p: "10px 12px",
      minWidth: 0,
      display: "flex", flexDirection: "column", gap: "4px",
    }}>
      <Typography sx={{
        fontSize: 10, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 20, fontWeight: 500, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sublabel && (
        <Typography sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1.2 }}>
          {sublabel}
        </Typography>
      )}
    </Box>
  );
}

export function SectionTitle({ children, count }) {
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1, mt: 2 }}>
      <Typography sx={{
        fontSize: 11, fontWeight: 500, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.06em",
      }}>
        {children}
      </Typography>
      {typeof count === "number" && (
        <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
          ({count})
        </Typography>
      )}
    </Box>
  );
}

export function EmptyState({ title, hint }) {
  return (
    <Box sx={{
      p: 4, textAlign: "center",
      border: "0.5px dashed", borderColor: "divider", borderRadius: 2,
      color: "text.secondary",
    }}>
      <Typography sx={{ fontSize: 13, fontWeight: 500, mb: 0.5, color: "text.primary" }}>
        {title}
      </Typography>
      {hint && (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}